import type { Streamer } from '../types/domain';

interface TwitchStream {
    user_id: string;
    [key: string]: unknown;
}

interface DataResponse<T> {
    data: T[];
}

interface AppConfig {
    twitch: {
        clientId: string;
        embedParents: string[];
    };
}

async function requestJson<T>(url: string, signal?: AbortSignal): Promise<T> {
    const response = await fetch(url, { signal });
    if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `Request failed (${response.status})`);
    }
    return response.json() as Promise<T>;
}

class TimedCache<T> {
    private readonly values = new Map<string, { value: T; expiresAt: number }>();

    constructor(private readonly ttlMs: number) {}

    get(key: string): T | undefined {
        const entry = this.values.get(key);
        if (!entry || entry.expiresAt <= Date.now()) {
            this.values.delete(key);
            return undefined;
        }
        return entry.value;
    }

    set(key: string, value: T): void {
        this.values.set(key, { value, expiresAt: Date.now() + this.ttlMs });
    }
}

export class TwitchApiClient {
    private readonly streamerCache = new TimedCache<Streamer>(60_000);
    private readonly streamCache = new TimedCache<TwitchStream>(60_000);
    private readonly pending = new Map<string, Promise<unknown>>();

    getConfig(): Promise<AppConfig> {
        return this.deduplicated('config', () => requestJson<AppConfig>('/api/config'));
    }

    private deduplicated<T>(key: string, request: () => Promise<T>): Promise<T> {
        const existing = this.pending.get(key) as Promise<T> | undefined;
        if (existing) return existing;
        const pending = request().finally(() => this.pending.delete(key));
        this.pending.set(key, pending);
        return pending;
    }

    async getStreamers(usernames: string[]): Promise<DataResponse<Streamer>> {
        const unique = [...new Set(usernames.map((name) => name.toLowerCase()))];
        const cached = unique.map((name) => this.streamerCache.get(name)).filter(Boolean) as Streamer[];
        const missing = unique.filter((name) => !this.streamerCache.get(name));
        if (!missing.length) return { data: cached };
        const query = missing.map((name) => `login=${encodeURIComponent(name)}`).join('&');
        const fetched = await this.deduplicated(`users:${[...missing].sort().join(',')}`, () =>
            requestJson<DataResponse<Streamer>>(`/api/twitch/users?${query}`),
        );
        fetched.data.forEach((streamer) => this.streamerCache.set(streamer.login.toLowerCase(), streamer));
        return { data: [...cached, ...fetched.data] };
    }

    async getStreamsInfo(userIds: string[]): Promise<DataResponse<TwitchStream>> {
        const unique = [...new Set(userIds)];
        const cached = unique.map((id) => this.streamCache.get(id)).filter(Boolean) as TwitchStream[];
        const missing = unique.filter((id) => !this.streamCache.get(id));
        if (!missing.length) return { data: cached };
        const query = missing.map((id) => `user_id=${encodeURIComponent(id)}`).join('&');
        const fetched = await this.deduplicated(`streams:${[...missing].sort().join(',')}`, () =>
            requestJson<DataResponse<TwitchStream>>(`/api/twitch/streams?${query}`),
        );
        fetched.data.forEach((stream) => this.streamCache.set(stream.user_id, stream));
        return { data: [...cached, ...fetched.data] };
    }
}

export const twitchApi = new TwitchApiClient();
